import React, { useState, useEffect, useCallback } from 'react';
import {
  Route, Plus, Pencil, Trash2, Power, PowerOff, RefreshCw, Search,
  ChevronDown, ChevronRight, X, Check, Loader2, Shield, Globe, Clock,
  Hash, Phone, Tag, Percent, AlertTriangle, Zap, Server, Database,
  ArrowRight, Copy, Eye, Filter, BarChart3, Settings
} from 'lucide-react';

const API = '/api/v1/admin/routing-policies';
const headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const POLICY_TYPES = [
  { value: 'PLAN_DEFAULT', label: 'Plan Default', icon: Tag, color: 'indigo', desc: 'Default route for users on a specific plan' },
  { value: 'USER_OVERRIDE', label: 'User Override', icon: Shield, color: 'purple', desc: 'Override route for specific users (highest priority)' },
  { value: 'HEADER_BASED', label: 'Header Based', icon: Hash, color: 'blue', desc: 'Route by sender ID / header name' },
  { value: 'ENTITY_BASED', label: 'Entity Based', icon: Tag, color: 'cyan', desc: 'Route by DLT entity ID' },
  { value: 'TEMPLATE_BASED', label: 'Template Based', icon: Filter, color: 'teal', desc: 'Route by DLT template ID' },
  { value: 'NUMBER_PREFIX', label: 'Number Prefix', icon: Phone, color: 'green', desc: 'Route by mobile number prefix (91, 9170, etc.)' },
  { value: 'KEYWORD_BASED', label: 'Keyword Based', icon: Search, color: 'pink', desc: 'Route by keyword in message content' },
  { value: 'OTP_PRIORITY', label: 'OTP Priority', icon: Zap, color: 'yellow', desc: 'High-priority route for OTP messages (auto-detected)' },
  { value: 'TIME_BASED', label: 'Time Based', icon: Clock, color: 'amber', desc: 'Route by time window (business hours, etc.)' },
  { value: 'PERCENTAGE_SPLIT', label: 'Percentage Split', icon: Percent, color: 'orange', desc: 'Split traffic across connectors by weight' },
  { value: 'FAILOVER', label: 'Failover', icon: AlertTriangle, color: 'red', desc: 'Automatic failover to backup connector' },
];

const getTypeInfo = (type) => POLICY_TYPES.find(t => t.value === type) || POLICY_TYPES[0];

const Badge = ({ children, color = 'gray' }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pink: 'bg-pink-50 text-pink-700 border-pink-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const RoutingPolicyManagement = () => {
  const [policies, setPolicies] = useState([]);
  const [connectors, setConnectors] = useState({});
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [cacheStats, setCacheStats] = useState(null);
  const [connectorList, setConnectorList] = useState([]);
  const [planList, setPlanList] = useState([]);
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const [polRes, connRes, planRes, statsRes] = await Promise.all([
        fetch(API, { headers: headers() }),
        fetch(`${API}/connectors`, { headers: headers() }),
        fetch(`${API}/plans`, { headers: headers() }),
        fetch(`${API}/cache-stats`, { headers: headers() }),
      ]);
      const polData = await polRes.json();
      setPolicies(polData.policies || []);
      setConnectors(polData.connectors || {});
      setPlans(polData.plans || {});

      const connData = await connRes.json();
      setConnectorList(Array.isArray(connData) ? connData : []);

      const planData = await planRes.json();
      setPlanList(Array.isArray(planData) ? planData : []);

      const statsData = await statsRes.json();
      setCacheStats(statsData);
    } catch (e) {
      showToast('Failed to load routing policies', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const handleCreate = () => {
    setEditingPolicy(null);
    setShowModal(true);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this routing policy?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/${id}`, { method: 'DELETE', headers: headers() });
      if (res.ok) { showToast('Policy deleted'); fetchPolicies(); }
      else { const d = await res.json(); showToast(d.error || 'Failed', 'error'); }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleToggle = async (id, currentActive) => {
    try {
      const res = await fetch(`${API}/${id}/toggle`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ is_active: !currentActive }),
      });
      if (res.ok) { showToast(`Policy ${!currentActive ? 'activated' : 'deactivated'}`); fetchPolicies(); }
    } catch { showToast('Failed to toggle', 'error'); }
  };

  const handleSyncCache = async () => {
    try {
      const res = await fetch(`${API}/sync-cache`, { method: 'POST', headers: headers() });
      if (res.ok) { showToast('Routing cache synced to Redis'); fetchPolicies(); }
    } catch { showToast('Sync failed', 'error'); }
  };

  const handleSave = async (formData) => {
    setActionLoading(true);
    try {
      const url = editingPolicy ? `${API}/${editingPolicy.id}` : API;
      const method = editingPolicy ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(formData) });
      const data = await res.json();
      if (res.ok) {
        showToast(editingPolicy ? 'Policy updated' : 'Policy created');
        setShowModal(false);
        fetchPolicies();
      } else {
        showToast(data.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const filtered = policies.filter(p => {
    if (filterType && p.policy_type !== filterType) return false;
    if (searchTerm && !p.policy_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getConnectorName = (id) => connectors[id] || `Connector #${id}`;
  const getPlanName = (id) => plans[id] || `Plan #${id}`;

  return (
    <div className="min-h-screen">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
        }`}>
          {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Route size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Routing Policy Management</h1>
              <p className="text-sm text-gray-500 mt-0.5">Enterprise-grade SMS routing with Redis-cached policies — zero delay</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSyncCache} className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors">
              <Database size={14} /> Sync Cache
            </button>
            <button onClick={fetchPolicies} disabled={loading} className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={handleCreate} className="flex items-center gap-1.5 px-4 py-2.5 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Plus size={16} /> New Policy
            </button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Policies', value: policies.length, icon: Route, color: '#4f46e5', bg: 'rgba(79,70,229,0.06)' },
          { label: 'Active', value: policies.filter(p => p.is_active).length, icon: Zap, color: '#059669', bg: 'rgba(5,150,105,0.06)' },
          { label: 'Connectors', value: connectorList.length, icon: Server, color: '#2563eb', bg: 'rgba(37,99,235,0.06)' },
          { label: 'Plans', value: planList.length, icon: Tag, color: '#7c3aed', bg: 'rgba(124,58,237,0.06)' },
          { label: 'Redis Cache', value: cacheStats?.policies_cached ? '✓ Synced' : '✗ Empty', icon: Database, color: cacheStats?.policies_cached ? '#059669' : '#ef4444', bg: cacheStats?.policies_cached ? 'rgba(5,150,105,0.06)' : 'rgba(239,68,68,0.06)' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: stat.bg }}>
                <Icon size={18} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-semibold">{stat.label}</p>
                <p className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Search policies..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
          />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400">
          <option value="">All Types</option>
          {POLICY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Policy List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-indigo-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <Route size={48} className="text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400 mb-4">No routing policies found</p>
          <button onClick={handleCreate} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600">
            Create First Policy
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(policy => {
            const typeInfo = getTypeInfo(policy.policy_type);
            const TypeIcon = typeInfo.icon;
            const isExpanded = expandedPolicy === policy.id;

            return (
              <div key={policy.id} className={`bg-white rounded-xl border shadow-sm transition-all ${
                policy.is_active ? 'border-gray-100' : 'border-red-100 opacity-70'
              }`}>
                {/* Policy Header */}
                <div className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    policy.is_active ? `bg-${typeInfo.color}-50` : 'bg-gray-100'
                  }`}>
                    <TypeIcon size={18} className={policy.is_active ? `text-${typeInfo.color}-500` : 'text-gray-400'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-800">{policy.policy_name}</span>
                      <Badge color={typeInfo.color}>{typeInfo.label}</Badge>
                      <span className="text-[10px] text-gray-400">Priority: {policy.priority}</span>
                      {!policy.is_active && <Badge color="red">Disabled</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {policy.targets?.length > 0 && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Server size={11} />
                          {policy.targets.map(t => t.connector_name || getConnectorName(t.connector_id)).join(', ')}
                        </span>
                      )}
                      {policy.scope_plan_ids?.length > 0 && (
                        <span className="text-xs text-gray-400">
                          Plans: {policy.scope_plan_ids.map(id => getPlanName(id)).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={e => { e.stopPropagation(); handleToggle(policy.id, policy.is_active); }}
                      className={`p-1.5 rounded-lg transition-colors ${policy.is_active ? 'hover:bg-red-50 text-emerald-500' : 'hover:bg-emerald-50 text-gray-400'}`}
                      title={policy.is_active ? 'Deactivate' : 'Activate'}>
                      {policy.is_active ? <Power size={15} /> : <PowerOff size={15} />}
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleEdit(policy); }}
                      className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500 transition-colors">
                      <Pencil size={15} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(policy.id); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                      <Trash2 size={15} />
                    </button>
                    {isExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-5 pb-4 border-t border-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      {/* Targets */}
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Target Connectors</p>
                        {policy.targets?.map((t, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                            <div className="flex items-center gap-2">
                              <Server size={12} className="text-indigo-500" />
                              <span className="text-xs font-medium text-gray-700">{t.connector_name || getConnectorName(t.connector_id)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400">Weight: {t.weight}%</span>
                              <span className="text-[10px] text-gray-400">P{t.priority}</span>
                            </div>
                          </div>
                        ))}
                        {policy.failover_connector_ids?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-50 rounded-lg">
                            <p className="text-[10px] font-bold text-amber-600 mb-1">Failover Connectors</p>
                            {policy.failover_connector_ids.map((id, i) => (
                              <span key={i} className="text-xs text-amber-700">{getConnectorName(id)}{i < policy.failover_connector_ids.length - 1 ? ', ' : ''}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Scope */}
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Scope</p>
                        {policy.scope_plan_ids?.length > 0 ? (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500 mb-1">Plans:</p>
                            <div className="flex flex-wrap gap-1">
                              {policy.scope_plan_ids.map(id => <Badge key={id} color="indigo">{getPlanName(id)}</Badge>)}
                            </div>
                          </div>
                        ) : <p className="text-xs text-gray-400 mb-2">All Plans (Global)</p>}
                        {policy.scope_user_ids?.length > 0 && (
                          <div>
                            <p className="text-[10px] text-gray-500 mb-1">Users:</p>
                            <div className="flex flex-wrap gap-1">
                              {policy.scope_user_ids.map(id => <Badge key={id} color="purple">User #{id}</Badge>)}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Match Conditions */}
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Match Conditions</p>
                        {policy.match_headers?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Headers:</p>
                            <div className="flex flex-wrap gap-1">{policy.match_headers.map((h, i) => <Badge key={i} color="blue">{h}</Badge>)}</div>
                          </div>
                        )}
                        {policy.match_entity_ids?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Entity IDs:</p>
                            <div className="flex flex-wrap gap-1">{policy.match_entity_ids.map((e, i) => <Badge key={i} color="cyan">{e}</Badge>)}</div>
                          </div>
                        )}
                        {policy.match_template_ids?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Template IDs:</p>
                            <div className="flex flex-wrap gap-1">{policy.match_template_ids.map((t, i) => <Badge key={i} color="teal">{t}</Badge>)}</div>
                          </div>
                        )}
                        {policy.match_number_prefixes?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Number Prefixes:</p>
                            <div className="flex flex-wrap gap-1">{policy.match_number_prefixes.map((p, i) => <Badge key={i} color="green">{p}*</Badge>)}</div>
                          </div>
                        )}
                        {policy.match_keywords?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Keywords:</p>
                            <div className="flex flex-wrap gap-1">{policy.match_keywords.map((k, i) => <Badge key={i} color="pink">{k}</Badge>)}</div>
                          </div>
                        )}
                        {policy.match_otp_pattern && (
                          <div className="mb-2">
                            <Badge color="yellow">OTP Auto-Detection Enabled</Badge>
                          </div>
                        )}
                        {policy.match_time_windows?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] text-gray-500">Time Windows:</p>
                            {policy.match_time_windows.map((tw, i) => (
                              <p key={i} className="text-xs text-gray-600">{tw.start} - {tw.end} (Days: {tw.days?.join(',')})</p>
                            ))}
                          </div>
                        )}
                        {!policy.match_headers?.length && !policy.match_entity_ids?.length && !policy.match_template_ids?.length && !policy.match_number_prefixes?.length && !policy.match_keywords?.length && !policy.match_otp_pattern && !policy.match_time_windows?.length && (
                          <p className="text-xs text-gray-400">No conditions — matches all traffic</p>
                        )}
                        {policy.description && (
                          <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">{policy.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Policy Type Legend */}
      <div className="mt-6 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-500 mb-3">Policy Type Reference</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {POLICY_TYPES.map(t => {
            const Icon = t.icon;
            return (
              <div key={t.value} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50">
                <Icon size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-700">{t.label}</p>
                  <p className="text-[10px] text-gray-400">{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 p-3 bg-indigo-50 rounded-lg">
          <p className="text-xs text-indigo-700">
            <strong>Evaluation Order:</strong> Policies are evaluated by priority (lowest number first). The first matching policy determines the route.
            All active policies are cached in Redis for zero-delay routing — no DB queries during SMS processing.
          </p>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <PolicyModal
          policy={editingPolicy}
          connectors={connectorList}
          plans={planList}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

// ─── Policy Create/Edit Modal ────────────────────────────────────────────────

const PolicyModal = ({ policy, connectors, plans, onSave, onClose, loading }) => {
  const [form, setForm] = useState({
    policy_name: policy?.policy_name || '',
    description: policy?.description || '',
    policy_type: policy?.policy_type || 'PLAN_DEFAULT',
    priority: policy?.priority ?? 100,
    is_active: policy?.is_active ?? true,
    scope_plan_ids: policy?.scope_plan_ids || [],
    scope_user_ids: policy?.scope_user_ids || [],
    match_headers: policy?.match_headers || [],
    match_entity_ids: policy?.match_entity_ids || [],
    match_template_ids: policy?.match_template_ids || [],
    match_number_prefixes: policy?.match_number_prefixes || [],
    match_keywords: policy?.match_keywords || [],
    match_otp_pattern: policy?.match_otp_pattern || false,
    match_time_windows: policy?.match_time_windows || [],
    targets: policy?.targets?.length > 0 ? policy.targets : [{ connector_id: 0, weight: 100, priority: 1 }],
    failover_connector_ids: policy?.failover_connector_ids || [],
    max_retries: policy?.max_retries || 0,
  });

  const [tagInputs, setTagInputs] = useState({
    headers: '', entities: '', templates: '', prefixes: '', keywords: '', userIds: '',
  });

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const addTag = (field, inputKey) => {
    const val = tagInputs[inputKey]?.trim();
    if (!val) return;
    const current = form[field] || [];
    if (!current.includes(val)) {
      update(field, [...current, val]);
    }
    setTagInputs(t => ({ ...t, [inputKey]: '' }));
  };

  const removeTag = (field, idx) => {
    update(field, form[field].filter((_, i) => i !== idx));
  };

  const addTarget = () => {
    update('targets', [...form.targets, { connector_id: 0, weight: 100, priority: form.targets.length + 1 }]);
  };

  const updateTarget = (idx, field, value) => {
    const targets = [...form.targets];
    targets[idx] = { ...targets[idx], [field]: field === 'connector_id' || field === 'weight' || field === 'priority' ? parseInt(value) || 0 : value };
    update('targets', targets);
  };

  const removeTarget = (idx) => {
    if (form.targets.length <= 1) return;
    update('targets', form.targets.filter((_, i) => i !== idx));
  };

  const togglePlanScope = (planId) => {
    const current = form.scope_plan_ids || [];
    if (current.includes(planId)) {
      update('scope_plan_ids', current.filter(id => id !== planId));
    } else {
      update('scope_plan_ids', [...current, planId]);
    }
  };

  const handleSubmit = () => {
    if (!form.policy_name) return;
    if (form.targets.length === 0 || form.targets.every(t => !t.connector_id)) return;
    // Clean up empty arrays
    const payload = { ...form };
    if (!payload.scope_plan_ids?.length) payload.scope_plan_ids = [];
    if (!payload.scope_user_ids?.length) payload.scope_user_ids = [];
    if (!payload.match_headers?.length) payload.match_headers = [];
    if (!payload.match_entity_ids?.length) payload.match_entity_ids = [];
    if (!payload.match_template_ids?.length) payload.match_template_ids = [];
    if (!payload.match_number_prefixes?.length) payload.match_number_prefixes = [];
    if (!payload.match_keywords?.length) payload.match_keywords = [];
    if (!payload.match_time_windows?.length) payload.match_time_windows = [];
    if (!payload.failover_connector_ids?.length) payload.failover_connector_ids = [];
    onSave(payload);
  };

  const typeInfo = getTypeInfo(form.policy_type);
  const showHeaders = ['HEADER_BASED'].includes(form.policy_type);
  const showEntities = ['ENTITY_BASED'].includes(form.policy_type);
  const showTemplates = ['TEMPLATE_BASED'].includes(form.policy_type);
  const showPrefixes = ['NUMBER_PREFIX'].includes(form.policy_type);
  const showKeywords = ['KEYWORD_BASED'].includes(form.policy_type);
  const showOTP = ['OTP_PRIORITY'].includes(form.policy_type);
  const showTimeWindows = ['TIME_BASED'].includes(form.policy_type);

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-800">{policy ? 'Edit Policy' : 'Create Routing Policy'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelClass}>Policy Name *</label>
              <input type="text" value={form.policy_name} onChange={e => update('policy_name', e.target.value)} className={inputClass} placeholder="e.g. SmartPing Default Route" />
            </div>
            <div>
              <label className={labelClass}>Policy Type *</label>
              <select value={form.policy_type} onChange={e => update('policy_type', e.target.value)} className={inputClass}>
                {POLICY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">{typeInfo.desc}</p>
            </div>
            <div>
              <label className={labelClass}>Priority (lower = higher priority)</label>
              <input type="number" min="1" max="999" value={form.priority} onChange={e => update('priority', parseInt(e.target.value) || 100)} className={inputClass} />
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Description</label>
              <input type="text" value={form.description} onChange={e => update('description', e.target.value)} className={inputClass} placeholder="Optional description" />
            </div>
          </div>

          {/* Target Connectors */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-700">Target Connectors *</p>
              <button onClick={addTarget} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium flex items-center gap-1">
                <Plus size={12} /> Add Target
              </button>
            </div>
            {form.targets.map((target, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <select value={target.connector_id} onChange={e => updateTarget(i, 'connector_id', e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400">
                  <option value={0}>Select Connector...</option>
                  {connectors.map(c => <option key={c.id} value={c.id}>{c.connector_name} ({c.host}:{c.port}) {c.is_active ? '✓' : '✗'}</option>)}
                </select>
                <input type="number" min="1" max="100" value={target.weight} onChange={e => updateTarget(i, 'weight', e.target.value)}
                  className="w-20 px-2 py-2 text-sm border border-gray-200 rounded-lg text-center" placeholder="Weight" title="Weight %" />
                <input type="number" min="1" max="10" value={target.priority} onChange={e => updateTarget(i, 'priority', e.target.value)}
                  className="w-16 px-2 py-2 text-sm border border-gray-200 rounded-lg text-center" placeholder="P" title="Priority" />
                {form.targets.length > 1 && (
                  <button onClick={() => removeTarget(i)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            <p className="text-[10px] text-gray-400 mt-1">Weight: traffic distribution %. Priority: failover order (1 = primary).</p>
          </div>

          {/* Plan Scope */}
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">Plan Scope</p>
            <p className="text-[10px] text-gray-400 mb-2">Select which plans this policy applies to. Leave empty for all plans (global).</p>
            <div className="flex flex-wrap gap-2">
              {plans.map(p => (
                <button key={p.id} onClick={() => togglePlanScope(p.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    form.scope_plan_ids?.includes(p.id)
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-indigo-200'
                  }`}>
                  {p.plan_name}
                </button>
              ))}
            </div>
          </div>

          {/* User Scope (for USER_OVERRIDE) */}
          {form.policy_type === 'USER_OVERRIDE' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">User IDs</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.userIds} onChange={e => setTagInputs(t => ({ ...t, userIds: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('scope_user_ids', 'userIds')}
                  className={inputClass} placeholder="Enter user ID and press Enter" />
                <button onClick={() => addTag('scope_user_ids', 'userIds')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.scope_user_ids?.map((id, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs">
                    User #{id} <button onClick={() => removeTag('scope_user_ids', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Match Conditions */}
          {showHeaders && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Match Headers (Sender IDs)</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.headers} onChange={e => setTagInputs(t => ({ ...t, headers: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('match_headers', 'headers')}
                  className={inputClass} placeholder="e.g. SMSIND" />
                <button onClick={() => addTag('match_headers', 'headers')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.match_headers?.map((h, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                    {h} <button onClick={() => removeTag('match_headers', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showEntities && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Match Entity IDs</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.entities} onChange={e => setTagInputs(t => ({ ...t, entities: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('match_entity_ids', 'entities')}
                  className={inputClass} placeholder="e.g. 1101000000..." />
                <button onClick={() => addTag('match_entity_ids', 'entities')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.match_entity_ids?.map((e, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded-full text-xs font-mono">
                    {e} <button onClick={() => removeTag('match_entity_ids', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showTemplates && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Match Template IDs</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.templates} onChange={e => setTagInputs(t => ({ ...t, templates: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('match_template_ids', 'templates')}
                  className={inputClass} placeholder="e.g. 1107161234567890" />
                <button onClick={() => addTag('match_template_ids', 'templates')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.match_template_ids?.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-mono">
                    {t} <button onClick={() => removeTag('match_template_ids', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showPrefixes && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Match Number Prefixes</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.prefixes} onChange={e => setTagInputs(t => ({ ...t, prefixes: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('match_number_prefixes', 'prefixes')}
                  className={inputClass} placeholder="e.g. 91, 9170, 44" />
                <button onClick={() => addTag('match_number_prefixes', 'prefixes')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.match_number_prefixes?.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-mono">
                    {p}* <button onClick={() => removeTag('match_number_prefixes', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showKeywords && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Match Keywords in Message</p>
              <p className="text-[10px] text-gray-400 mb-2">Messages containing any of these keywords will be routed through this policy.</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInputs.keywords} onChange={e => setTagInputs(t => ({ ...t, keywords: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addTag('match_keywords', 'keywords')}
                  className={inputClass} placeholder="e.g. OTP, verification, password" />
                <button onClick={() => addTag('match_keywords', 'keywords')} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium">Add</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.match_keywords?.map((k, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full text-xs">
                    {k} <button onClick={() => removeTag('match_keywords', i)}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showOTP && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">OTP Auto-Detection</p>
              <p className="text-[10px] text-gray-400 mb-2">Automatically detects OTP messages by pattern matching (e.g. 4-8 digit codes, "OTP", "verification code", "one-time password").</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.match_otp_pattern}
                  onChange={e => update('match_otp_pattern', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
                <span className="text-sm text-gray-700 font-medium">Enable OTP auto-detection for this policy</span>
              </label>
            </div>
          )}

          {/* Failover */}
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">Failover Configuration</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Failover Connectors</label>
                <select multiple value={form.failover_connector_ids?.map(String) || []}
                  onChange={e => update('failover_connector_ids', Array.from(e.target.selectedOptions, o => parseInt(o.value)))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg h-20">
                  {connectors.map(c => <option key={c.id} value={c.id}>{c.connector_name}</option>)}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Hold Ctrl to select multiple</p>
              </div>
              <div>
                <label className={labelClass}>Max Retries</label>
                <input type="number" min="0" max="5" value={form.max_retries} onChange={e => update('max_retries', parseInt(e.target.value) || 0)} className={inputClass} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSubmit} disabled={loading || !form.policy_name}
            className="px-4 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {policy ? 'Update Policy' : 'Create Policy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutingPolicyManagement;
